import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { UPDATE_AI_ANALYSIS } from '../../GraphQL/AIAnalysis/mutations';
import { GET_AI_ANALYSIS_STEPS } from '../../GraphQL/AIAnalysis/queries';
import './AIAnalysisMenu.css';

export default function AIAnalysisMenu() {
  const data = useSelector((state) => state.aiAnalysis.data);
  const [toastRef, setToastRef] = useState(null);

  const [editingRowId, setEditingRowId] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedFalsePositive, setEditedFalsePositive] = useState(false);

  const [updateAIAnalysis] = useMutation(UPDATE_AI_ANALYSIS, {
    refetchQueries: [{ query: GET_AI_ANALYSIS_STEPS }],
    awaitRefetchQueries: true,
    onCompleted: (result) => {
      toastRef?.show({
        severity: 'info',
        summary: 'Edited',
        detail: 'Status edited successfully',
        contentClassName: 'custom-blue-toast',
      });
    },
    onError: (error) => {
      toastRef?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update status',
      });
    },
  });

  const statusBodyTemplate = (rowData) => {
    const status = rowData.metadata?.status || 'PENDING';
    let severity;
    let style = {};

    switch (status) {
      case 'VERIFIED':
        severity = 'success';
        style = { backgroundColor: 'var(--passed-status)' };
        break;
      case 'REJECTED':
        severity = 'danger';
        style = { backgroundColor: 'var(--failed-status)' };
        break;
      case 'PENDING':
      default:
        severity = 'secondary';
        style = { backgroundColor: 'var(--not-executed-status)' };
        break;
    }

    return <Badge value={status} severity={severity} style={style} />;
  };

  const falsePositiveBodyTemplate = (rowData) => {
    const isEditing = editingRowId === rowData._id;

    return (
      <div className='false-positive-container'>
        {isEditing ? (
          <InputSwitch
            checked={editedFalsePositive}
            onChange={(e) => setEditedFalsePositive(e.value)}
            tooltip='Toggle false positive'
          />
        ) : rowData.isFalsePositive ? (
          <Badge value='Yes' severity='success' />
        ) : (
          <Badge value='No' severity='secondary' />
        )}
      </div>
    );
  };

  const embeddingTextBodyTemplate = (rowData) => {
    const text = rowData.embeddingText || '';
    const truncatedText =
      text.length > 100 ? `${text.substring(0, 100)}...` : text;
    const tooltipId = `embedding-tooltip-${rowData._id}`;

    return (
      <>
        <div
          id={tooltipId}
          className={tooltipId}
          style={{
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: text.length > 100 ? 'help' : 'default',
          }}
        >
          {truncatedText}
        </div>
        {text.length > 100 && (
          <Tooltip
            target={`#${tooltipId}`}
            content={text}
            position='bottom'
            style={{ maxWidth: '250px' }}
          />
        )}
      </>
    );
  };

  const historicalAnalysisBodyTemplate = (rowData) => {
    const isEditing = editingRowId === rowData._id;
    const text = isEditing
      ? editedDescription
      : rowData.historicalAiAnalysis || '';

    if (isEditing) {
      return (
        <InputTextarea
          value={editedDescription}
          onChange={(e) => setEditedDescription(e.target.value)}
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
          placeholder='Enter AI analysis description...'
        />
      );
    }

    const truncatedText =
      text.length > 100 ? `${text.substring(0, 100)}...` : text;
    const tooltipId = `historical-tooltip-${rowData._id}`;

    return (
      <>
        <div
          id={tooltipId}
          className={tooltipId}
          style={{
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: text.length > 100 ? 'help' : 'default',
          }}
        >
          {truncatedText || 'Not Available'}
        </div>
        {text.length > 100 && (
          <Tooltip
            target={`#${tooltipId}`}
            content={text}
            position='bottom'
            style={{ maxWidth: '250px' }}
          />
        )}
      </>
    );
  };

  const handleStatusUpdate = (rowData, status) => {
    const statusText = status === 'VERIFIED' ? 'verify' : 'reject';

    confirmDialog({
      message: `Are you sure you want to ${statusText} this AI analysis?`,
      header: `${status === 'VERIFIED' ? 'Verify' : 'Reject'} Analysis`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        updateAIAnalysis({
          variables: {
            record: {
              metadata: {
                status: status,
              },
            },
            id: rowData._id,
          },
        });
      },
    });
  };

  const actionsBodyTemplate = (rowData) => {
    const isEditing = editingRowId === rowData._id;

    const handleViewTestResults = () => {
      if (rowData.testExecutionId) {
        const url = `/trfx-show-test-results/${rowData.testExecutionId}`;
        window.open(url, '_blank');
      }
    };

    const handleEdit = () => {
      setEditingRowId(rowData._id);
      setEditedDescription(rowData.historicalAiAnalysis || '');
      setEditedFalsePositive(rowData.isFalsePositive);
    };

    const handleCancel = () => {
      setEditingRowId(null);
      setEditedDescription('');
      setEditedFalsePositive(false);
    };

    const handleSave = () => {
      const changes = [];

      if (editedDescription !== (rowData.historicalAiAnalysis || '')) {
        changes.push(`Description: "${editedDescription}"`);
      }

      if (editedFalsePositive !== rowData.isFalsePositive) {
        changes.push(
          `False Positive: ${editedFalsePositive ? 'Positive' : 'Negative'}`
        );
      }

      if (changes.length === 0) {
        handleCancel();
        return;
      }

      confirmDialog({
        message: (
          <div>
            <p>Save changes?</p>
            <ul style={{ textAlign: 'left', marginTop: '10px' }}>
              {changes.map((change, index) => (
                <li key={index}>{change}</li>
              ))}
            </ul>
          </div>
        ),
        header: 'Confirm Changes',
        icon: 'pi pi-question-circle',
        accept: () => {
          updateAIAnalysis({
            variables: {
              id: rowData._id,
              record: {
                historicalAiAnalysis: editedDescription,
                isFalsePositive: editedFalsePositive,
              },
            },
          });
          handleCancel();
        },
      });
    };

    const currentStatus = rowData.metadata?.status;

    return (
      <div className='flex gap-1'>
        <Button
          label='View'
          icon='pi pi-external-link'
          className='p-button-sm p-button-outlined button-view'
          onClick={handleViewTestResults}
          tooltip='View Test Results in New Tab'
          disabled={!rowData.testExecutionId}
        />
        {!isEditing ? (
          <>
            <Button
              label='Verify'
              icon='pi pi-check'
              className='p-button-sm p-button-success button-verify'
              onClick={() => handleStatusUpdate(rowData, 'VERIFIED')}
              tooltip='Mark as Verified'
              disabled={currentStatus === 'VERIFIED'}
            />
            <Button
              label='Reject'
              icon='pi pi-times'
              className='p-button-sm p-button-danger button-reject'
              onClick={() => handleStatusUpdate(rowData, 'REJECTED')}
              tooltip='Mark as Rejected'
              disabled={currentStatus === 'REJECTED'}
            />
            <Button
              icon='pi pi-pencil'
              className='p-button-text'
              style={{
                border: 'none',
                background: 'transparent',
                boxShadow: 'none',
                padding: '0.50rem',
                minWidth: 'auto',
              }}
              onClick={handleEdit}
              tooltip='Edit Analysis'
            />
          </>
        ) : (
          <>
            <Button
              label='Save'
              icon='pi pi-check'
              className='p-button-sm p-button-success'
              onClick={handleSave}
              tooltip='Save Changes'
            />
            <Button
              label='Cancel'
              icon='pi pi-times'
              className='p-button-sm p-button-secondary'
              onClick={handleCancel}
              tooltip='Cancel Edit'
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className='p-4' style={{ display: 'flex', justifyContent: 'center' }}>
      <Toast ref={setToastRef} />
      <ConfirmDialog />
      <Card title='AI Analysis Data' className='mb-4' style={{ width: '80%' }}>
        <DataTable
          value={data}
          paginator
          rows={10}
          className='p-datatable-sm'
          emptyMessage='No AI analysis data found'
          sortMode='multiple'
          removableSort
        >
          <Column
            field='metadata.status'
            header='Status'
            body={statusBodyTemplate}
            sortable
            style={{ width: '120px' }}
          />

          <Column
            field='embeddingText'
            header='Embedding Text'
            body={embeddingTextBodyTemplate}
            style={{ width: '300px' }}
          />

          <Column
            field='isFalsePositive'
            header='False Positive'
            body={falsePositiveBodyTemplate}
            sortable
            style={{ width: '100px' }}
          />
          <Column
            field='historicalAiAnalysis'
            header='Historical Analysis'
            body={historicalAnalysisBodyTemplate}
            sortable
            style={{ width: '300px' }}
          />
          <Column
            header='Actions'
            body={actionsBodyTemplate}
            style={{ width: '300px' }}
          />
        </DataTable>
      </Card>
    </div>
  );
}
